import React from 'react';
import { useAssistantWidget } from '../../contexts/assistant-widget-context';
import { useFi } from '../../contexts/fi-context';
import { usePeersona } from '../../contexts/peersona-context';
import { useEligiblePeersonas } from '../../hooks/api/useEligiblePeersonas';
import { PeersonaAvatar } from '../peersona/peersona-avatar';
import { PeersonaSparkle } from '../peersona/peersona-sparkle';
import { PEERSONA_TEXT_MUTED } from '../peersona/peersona-styles';
import { ConversationRow } from './conversation-row';
import { FiPinnedRow, ListViewMessage, PeersonasSectionHeader } from './list-view-bits';

/**
 * "Start a new conversation" list. Same row shape as the Conversations
 * view, but rows show ``scope_line`` and clicks open a fresh thread.
 */
export const NewConversationListView: React.FC = () => {
  const { switchContext, openChatView } = useAssistantWidget();
  const { resetSession } = useFi();
  const { openPanelForChatbot, startNewChat } = usePeersona();
  const { isLoading, peersonas } = useEligiblePeersonas();

  const handleFiClick = () => {
    // KINDLY-1022: "Start fresh" must end the active Fi thread and clear local
    // state — switching context alone left the previous conversation on screen.
    void resetSession();
    switchContext({ kind: 'fi' });
    openChatView();
  };

  const handlePeersonaClick = (chatbotId: number) => {
    openPanelForChatbot(chatbotId);
    startNewChat();
    openChatView();
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4">
      <FiPinnedRow body="Start fresh with the concierge" onClick={handleFiClick} />
      <PeersonasSectionHeader />
      {isLoading && peersonas.length === 0 ? (
        <ListViewMessage>Loading&hellip;</ListViewMessage>
      ) : peersonas.length === 0 ? (
        <ListViewMessage>No Peersonas available yet.</ListViewMessage>
      ) : (
        <div className="flex flex-col">
          {peersonas.map((p) => (
            <ConversationRow
              key={p.chatbotId}
              avatar={<PeersonaAvatar size={48} src={p.avatarUrl} name={p.name} />}
              titleLeading={<PeersonaSparkle size={12} style={{ color: PEERSONA_TEXT_MUTED }} />}
              title={p.name}
              body={p.scopeLine || `Talk with ${p.name}`}
              ariaLabel={`Start a new conversation with ${p.name}`}
              onClick={() => handlePeersonaClick(p.chatbotId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
